import React, { useEffect, useState } from 'react';

import { Typography, Box, Divider, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';

import { useAppSelector, useAppDispatch, usePrevious } from 'hooks';

import { antennasActions } from 'state/antennas';
import { satellitesActions } from 'state/satellites';
import { schedulesActions } from 'state/schedules';
import { switchesActions } from 'state/switches';
import { sensorsActions } from 'state/sensors';

import VisualiseActivityController from 'controllers/VisualiseGSActivity';

import ListDetailsPage from 'components/ListDetailsPage';
import AntennaOverview from 'components/antennas/Overview';
import AntennaListItem from 'components/antennas/ListItem';
import ScheduleSummary from 'components/schedules/Summary';
import SendCommandForm from 'components/groundstations/SendCommandForm';
import MapView from 'components/map/MapView';
import Switch from 'components/switches/Switch';
import PlotGroup from 'components/sensors/PlotGroup';
import DisplayLogs from 'components/groundstations/DisplayLogs';
import UpdatePosition from 'components/groundstations/UpdatePosition';
import ScheduleDetail from 'components/schedules/Detailed';
import GetStepPlan from 'components/groundstations/GetStepPlan';

import { AntennaManager, ScheduleManager } from 'models';
import { MapControl } from 'controllers';
import ScheduleSummaryList from 'components/schedules/ScheduleSummaryList';
import TLEDetails from 'components/schedules/TLEDetails';

const GROUND_STATION_REFRESH_RATE = 1000 * 60 * 2; // seconds

const SHOW_GLOBE_KEY = 'show_globe';

interface PageProps {
    isRefreshing?: boolean;
    setisRefreshing: React.Dispatch<React.SetStateAction<boolean>>;
}

const GroundStationOverviewPage: React.FunctionComponent<PageProps> = ({
    isRefreshing,
    setisRefreshing,
}) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();

    const {
        auth,
        antennas,
        schedules: schedulesObject,
        satellites,
        switches,
    } = useAppSelector((state) => state);

    const schedules = schedulesObject.list.object;

    const [tabValue, setTabValue] = useState('schedules');

    const [showGlobe, setShowGlobe] = useState(
        localStorage.getItem(SHOW_GLOBE_KEY) ?? 'show',
    );

    const [nextSchedule, setNextSchedule] =
        useState<ScheduleManager.Schedule | null>(null);

    const [selectedAntennaId, setSelectedAntennaId] =
        useState<AntennaManager.Antenna['id']>();

    const [foundAntennaId, setFoundAntennaId] =
        useState<AntennaManager.Antenna['id']>();

    const [selectedAntenna, setSelectedAntenna] =
        useState<AntennaManager.Antenna>();

    const prevSelectedAntennaId = usePrevious(selectedAntennaId);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [mapDispatch, setMapDispatch] =
        useState<(control: MapControl) => void>();

    const [visualiser, setVisualiser] = useState<VisualiseActivityController>();

    const [searchText, setSearchText] = useState('');

    const handleMapRegistration = (
        mapDispatcher: (control: MapControl) => void,
    ) => {
        setMapDispatch(mapDispatcher);

        const v = new VisualiseActivityController(
            selectedAntenna ? [selectedAntenna] : [],
            dispatch,
            0,
        );

        setVisualiser(v);

        mapDispatcher(v);
        console.log('Map Registration');
    };

    const getSchedules = () => {
        dispatch(
            schedulesActions.getScheduleList({
                antenna: selectedAntennaId,
                limit: 6,
                after_stop_time: new Date(
                    Date.now() - 1000 * 60 * 10,
                ).toISOString(),
                ordering: 'start_time',
                status: 'booked',
            }),
        );
    };

    const getNextScheduleFromAntenna = async (scheduleId: number) => {
        return dispatch(schedulesActions.getSchedule(scheduleId));
    };

    const refreshAntenna = () => {
        if (!isRefreshing) {
            dispatch(antennasActions.getAntennaList()).then(() => {
                setisRefreshing(false);
                getSchedules();
            });
            setisRefreshing(true);
        }
    };

    useEffect(() => {
        if (prevSelectedAntennaId !== selectedAntennaId)
            setFoundAntennaId(
                antennas.list.object?.find((a) => a.id === selectedAntennaId)
                    ?.id,
            );

        setSelectedAntenna(
            antennas.list.object?.find((a) => a.id === selectedAntennaId),
        );
    }, [selectedAntennaId, antennas.list]);

    useEffect(() => {
        if (
            auth.tokens.status === 'finished' &&
            selectedAntennaId != null &&
            prevSelectedAntennaId !== selectedAntennaId
        ) {
            getSchedules();
        }
    }, [selectedAntennaId, auth.tokens.status]);

    useEffect(() => {
        if (selectedAntenna?.next_schedule) {
            getNextScheduleFromAntenna(selectedAntenna.next_schedule).then(
                (schedule) => {
                    setNextSchedule(schedule);
                },
            );
        } else {
            setNextSchedule(null);
        }
    }, [selectedAntenna?.next_schedule]);

    useEffect(() => {
        if (selectedAntenna) {
            dispatch(
                switchesActions.getSwitchList({
                    groundStationId: selectedAntenna.groundstation.id,
                }),
            );
            dispatch(
                sensorsActions.getSensorList({
                    groundStationId: selectedAntenna.groundstation.id,
                }),
            );
            dispatch(sensorsActions.getSensorFieldList());

            visualiser?.updateAntennas([selectedAntenna], 0);
        } else {
            visualiser?.clear();
        }
    }, [foundAntennaId]);

    useEffect(() => {
        let handler: NodeJS.Timeout | null = null;

        if (
            auth.tokens.status === 'finished' &&
            satellites.list.status === 'idle'
        )
            dispatch(satellitesActions.getSatelliteList());

        if (
            auth.tokens.status === 'finished' &&
            antennas.list.status === 'idle'
        ) {
            dispatch(antennasActions.getAntennaList()).then(() => {
                setisRefreshing(false);
            });
            setisRefreshing(true);

            handler = setInterval(() => {
                if (!isRefreshing) {
                    dispatch(antennasActions.getAntennaList()).then(() => {
                        setisRefreshing(false);
                    });
                    setisRefreshing(true);
                }
            }, GROUND_STATION_REFRESH_RATE);
        }

        return () => {
            if (handler) clearInterval(handler);
        };
    }, [auth.tokens.status]);

    const SchedulesDetails = (
        <Box
            sx={{
                my: 1,
                py: 1,
            }}
        >
            <Box
                sx={{
                    display: 'grid',
                    gap: 1,
                    [theme.breakpoints.down('xl')]: {
                        gridTemplateRows: 'auto auto',
                    },
                    [theme.breakpoints.up('xl')]: {
                        gridTemplateColumns: '1fr 1fr',
                    },
                }}
            >
                <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Next Pass Details
                    </Typography>
                    {nextSchedule ? (
                        <Box sx={{ display: 'grid', gap: 1 }}>
                            <ScheduleDetail
                                schedule={nextSchedule}
                                context="groundstation"
                            />
                            <TLEDetails schedule={nextSchedule} />
                        </Box>
                    ) : (
                        <Typography variant="h6" align="center" sx={{ m: 2 }}>
                            The Next Schedule has not been Selected by the
                            Ground Station
                        </Typography>
                    )}
                </Box>

                <Box
                    sx={{
                        overflow: 'auto',
                        maxHeight: '100%',
                        minHeight: 320,
                        display: 'grid',
                        gap: 1,
                        columns: 2,
                        width: '100%',
                    }}
                >
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Antennas Next Schedules
                    </Typography>
                    {schedules &&
                        schedules.length > 0 &&
                        schedules.map((s) => (
                            <ScheduleSummary
                                key={`${s.id}`}
                                schedule={s}
                                context="groundstation"
                            />
                        ))}
                    {schedules && schedules.length === 0 && (
                        <Box>
                            <Typography
                                variant="h6"
                                align="center"
                                sx={{ m: 2 }}
                            >
                                No Schedule&apos;s assigned to this antenna.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );

    return (
        <ListDetailsPage
            title="Ground Station Antenna Details"
            breadcrumbs={[
                { text: 'Home', link: '/' },
                { text: 'Ground Station Overview' },
            ]}
            onSearchChange={(text) => setSearchText(text)}
            list={antennas.list.object
                ?.filter((a) => {
                    if (!searchText) return true;

                    const search = searchText.toUpperCase();

                    return (
                        a.name.toUpperCase().includes(search) ||
                        a.groundstation.name.toUpperCase().includes(search) ||
                        a.status.toUpperCase().includes(search)
                    );
                })
                .map((a) => (
                    <AntennaListItem
                        key={a.id}
                        antenna={a}
                        onClick={() => {
                            setSelectedAntennaId(a.id);
                        }}
                    />
                ))}
        >
            {selectedAntenna && (
                <>
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 0,
                            [theme.breakpoints.down(1850)]: {
                                gridTemplateRows: 'auto auto',
                            },
                            [theme.breakpoints.up(1850)]: {
                                gridTemplateColumns: 'auto 1fr',
                                gap: 1,
                            },
                        }}
                    >
                        <TabContext value={showGlobe}>
                            <Box>
                                <AntennaOverview
                                    antenna={selectedAntenna}
                                    onRefreshClick={refreshAntenna}
                                    isRefreshing={isRefreshing}
                                />
                                <TabList
                                    onChange={(_, newValue) => {
                                        setShowGlobe(newValue);
                                        localStorage.setItem(
                                            SHOW_GLOBE_KEY,
                                            `${newValue}`,
                                        );
                                    }}
                                    aria-label="Show/Hide Globe"
                                    variant="scrollable"
                                >
                                    <Tab label="Show Globe" value="show" />
                                    <Tab label="Hide Globe" value="hide" />
                                </TabList>
                            </Box>
                            <TabPanel value="show" sx={{ p: 0 }}>
                                <Box sx={{ height: 480, mb: 0 }}>
                                    <MapView
                                        registerMapControl={
                                            handleMapRegistration
                                        }
                                    />
                                </Box>
                            </TabPanel>
                            <TabPanel value="hide" sx={{ p: 0 }}></TabPanel>
                        </TabContext>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <TabContext value={tabValue}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <TabList
                                onChange={(_, newValue) =>
                                    setTabValue(newValue)
                                }
                                aria-label="Ground Station Tabs"
                                variant="scrollable"
                            >
                                <Tab label="Schedules" value="schedules" />
                                <Tab label="Command & Control" value="c&c" />
                                <Tab label="Sensors" value="sensors" />
                                <Tab label="Logs" value="logs" />
                                <Tab label="Step Plan" value="stepplan" />
                                <Tab
                                    label="All Upcoming"
                                    value="upcoming-all"
                                />
                                <Tab label="Historic" value="historic" />
                            </TabList>
                        </Box>

                        <TabPanel value="schedules">
                            {SchedulesDetails}
                        </TabPanel>
                        <TabPanel value="c&c">
                            <Box
                                sx={{
                                    display: 'grid',
                                    mt: 1,
                                    gap: 2,
                                    [theme.breakpoints.down('lg')]: {
                                        gridTemplateRows: 'auto auto',
                                    },
                                    [theme.breakpoints.up('lg')]: {
                                        gridTemplateColumns: '1fr 1fr',
                                    },
                                }}
                            >
                                <UpdatePosition antenna={selectedAntenna} />
                                {nextSchedule ? (
                                    <ScheduleDetail
                                        schedule={nextSchedule}
                                        context="groundstation"
                                    />
                                ) : (
                                    <Paper>
                                        <Typography
                                            variant="h6"
                                            align="center"
                                            sx={{ m: 2 }}
                                        >
                                            The Next Schedule has not been
                                            Selected by the Ground Station
                                        </Typography>
                                    </Paper>
                                )}
                                <SendCommandForm
                                    groundstation={
                                        selectedAntenna.groundstation
                                    }
                                    disabled={isRefreshing}
                                />
                                <Box
                                    sx={{
                                        display: 'grid',
                                        height: 'fit-content',
                                        gap: 1,
                                        [theme.breakpoints.down('xl')]: {
                                            gridTemplateRows: 'auto auto',
                                        },
                                        [theme.breakpoints.up('xl')]: {
                                            gridTemplateColumns: '1fr 1fr',
                                        },
                                    }}
                                >
                                    {(selectedAntenna &&
                                        switches.listByGroundStation[
                                            selectedAntenna.groundstation.id
                                        ]?.object?.map((switchObj) => (
                                            <Switch
                                                key={switchObj.switchId}
                                                obj={switchObj}
                                            />
                                        ))) ?? (
                                        <Typography>
                                            No Switches Available for this
                                            Ground Station
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                        </TabPanel>
                        <TabPanel value="sensors">
                            <Box
                                sx={{
                                    my: 1,
                                    py: 1,
                                }}
                            >
                                <PlotGroup
                                    groundStation={
                                        selectedAntenna.groundstation
                                    }
                                />
                            </Box>
                        </TabPanel>
                        <TabPanel value="logs">
                            <DisplayLogs
                                groundstation={selectedAntenna.groundstation}
                            />
                        </TabPanel>
                        <TabPanel value="stepplan">
                            <GetStepPlan
                                groundstation={selectedAntenna.groundstation}
                            />
                        </TabPanel>
                        <TabPanel value="upcoming-all">
                            <ScheduleSummaryList
                                context="groundstation"
                                antenna={selectedAntennaId}
                                ordering="start_time"
                                after_start_time={new Date().toISOString()}
                            />
                        </TabPanel>
                        <TabPanel value="historic">
                            <ScheduleSummaryList
                                context="groundstation"
                                antenna={selectedAntennaId}
                                ordering="-start_time"
                                before_start_time={new Date().toISOString()}
                            />
                        </TabPanel>
                    </TabContext>
                </>
            )}
            {!selectedAntenna && selectedAntennaId == null && (
                <Typography variant="h6" align="center" sx={{ m: 2 }}>
                    Select An Antenna
                </Typography>
            )}
            {!selectedAntenna && selectedAntennaId != null && (
                <Typography variant="h6" align="center" sx={{ m: 2 }}>
                    Cannot find Antenna with Id {selectedAntennaId}
                </Typography>
            )}
        </ListDetailsPage>
    );
};

GroundStationOverviewPage.displayName = 'GroundStationOverviewPage';
export default GroundStationOverviewPage;
